<template>
  <div
    id="app"
    :data-conference="getCity !== undefined ? getCity : false"
    :data-page="getRouteName"
    :class="[placeholderVisible ? 'app--fix' : false]"
  >
    <HeaderComp :menuOpen="menuOpen" />

    <div
      class="page-container"
      ref="smooth"
      :style="{ transform: `translate3d(0, -${translate}px, 0)` }"
    >
      <transition mode="out-in" @enter="enter" @leave="leave" appear>
        <router-view
          :key="`${getCity}/${getRouteName}`"
          :translate="getRouteName === 'speakers' ? translate : 0"
        />
      </transition>

      <FooterComp
        v-if="$store.state.pages[getCity] !== undefined && !getStateLoader" :openModal="openModal"
      />
    </div>

    <transition @enter="enterModal" @leave="leaveModal">
      <ModalConduct v-if="showModal" :closeModal="closeModal" />
    </transition>

    <cookie-law theme="min" buttonText="Ok" transitionName="fade" buttonClass="cookie-button">
      <div slot="message">
        This website uses cookies to ensure you get the best experience on our website. <a href="https://www.awwwards.com/cookies-policy/" target="_blank" rel="noopener">Learn More.</a>
      </div>
    </cookie-law>

    <div class="loader" v-show="getStateLoader" ref="loader"></div>

    <div v-if="!isMobileDevice()" class="cursor" id="cursor"></div>
  </div>
</template>

<script>
import anime from 'animejs';
import VirtualScroll from 'virtual-scroll';
import inobounce from 'inobounce';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';

import CookieLaw from 'vue-cookie-law';

import loop from '@/scripts/loop';
import { qs, lottieAnimation, getRandom, lerp, roundDec, isMobileDevice, isSafari } from '@/scripts/utils';

import HeaderComp from '@/components/ui/HeaderComp.vue';
import FooterComp from '@/components/ui/FooterComp.vue';
import ModalConduct from '@/components/ModalConduct.vue';

import cursor from '@/scripts/cursor';

import transAms1 from '@/assets/data/transitions/ams-1.json';
import transAms2 from '@/assets/data/transitions/ams-2.json';
import transAms3 from '@/assets/data/transitions/ams-3.json';
import transSf1 from '@/assets/data/transitions/sf-1.json';
import transSf2 from '@/assets/data/transitions/sf-2.json';
import transSf3 from '@/assets/data/transitions/sf-3.json';
import transNy1 from '@/assets/data/transitions/ny-1.json';
import transNy2 from '@/assets/data/transitions/ny-2.json';

export default {
  name: 'app',

  components: {
    HeaderComp,
    FooterComp,
    ModalConduct,
    CookieLaw,
  },

  data() {
    return {
      baseUrl: process.env.BASE_URL,
      checkScroll: true,
      handleRedirect: null,
      domRendered: false,

      transitions: [
        [
          transAms1,
          transAms2,
          transAms3,
        ],
        [
          transSf1,
          transSf2,
          transSf3,
        ],
        [
          transNy1,
          transNy2,
        ],
      ],
      transition: null,
      frames: {},
      currentTransitionIndex: 0,
      currentIndexCity: 0,

      showModal: false,
      menuOpen: false,

      isFirst: true,
      isMobileDevice,

      scrollY: 0,
      interScrollY: 0,
      viewportH: 0,
      translate: 0,
    };
  },

  computed: {
    ...mapGetters(['getCity', 'getRouteName', 'getStateLoader', 'getSpeakerName']),

    placeholderVisible() {
      return isSafari();
    },
  },

  watch: {
    getRouteName(newVal, oldVal) {
      if (oldVal !== null) {
        this.isFirst = false;
      }
    },

    menuOpen(newVal) {
      if (newVal) {
        document.body.classList.add('menu-opened');
      } else if (!newVal) {
        document.body.classList.remove('menu-opened');
      }
    },

    getCity(newVal, oldVal) {
      const city = newVal !== undefined ? newVal : oldVal;
      this.setCurrentCityIndex(city);
    },
  },

  created() {
    window.lerpedScrollY = 0;
    window.addEventListener('resize', debounce(() => this.resize(), 200));

    this.handleRedirect = () => { this.$router.push({ name: 'ErrorPage' }); };
    this.$eventHub.$on('redirect404', this.handleRedirect);
    this.$eventHub.$on('loading', this.doLoader);
    this.$eventHub.$on('scrollTo', val => this.scrollTo(val));
    this.$eventHub.$on('setTo', val => this.setTo(val));
    this.$eventHub.$on('domRendered', (val) => { this.domRendered = val; });

    this.setCurrentCityIndex(this.getCity);
  },

  mounted() {
    loop.start();

    // this.setAnimation();
    if (!this.isMobileDevice()) cursor(loop);

    this.scrollContext = this.$el.querySelector('[js-smooth]');
    this.virtualScroll = new VirtualScroll({
      mouseMultiplier: 0.4,
      touchMultiplier: 4,
      passive: true,
    });

    if (isSafari()) {
      window.addEventListener('scroll', this.scrollSafari);
    } else {
      this.virtualScroll.on(this.scroll);
      loop.add(this.checkSmooth, 'checkSmooth');
    }

    inobounce.enable();
  },

  methods: {
    resize() {
      this.$eventHub.$emit('windowResize');
    },

    setCurrentCityIndex(city) {
      this.currentIndexCity = city === 'amsterdam' ? 0 : 1;
    },

    setAnimation() {
      this.transition = lottieAnimation(this.$refs.loader, this.transitions[this.currentIndexCity][this.getRandomTransition()]);
      const frames = this.transition.getDuration(true);

      this.frames = {
        total: frames,
        limitEnter: Math.round(frames / 2),
        limitLeave: frames - Math.round(frames / 2),
        totalTime: this.transition.getDuration(false),
      };
    },

    getRandomTransition() {
      const prevTransitionIndex = this.currentTransitionIndex;
      const randomNumber = getRandom(0, (this.transitions[this.currentIndexCity].length - 1));

      this.currentTransitionIndex = (randomNumber === prevTransitionIndex) ? randomNumber === 0 ? 1 : 0 : randomNumber;

      return this.currentTransitionIndex;
    },

    enter(el, done) {
      if (this.isFirst) {
        this.$store.commit('SET_STATE_LOADER', false);
        done();
      } else {
        anime({
          target: el,
          duration: 300,
          opacity: 1,
          easing: 'easeInOutSine',
        });
        this.transition.playSegments([this.frames.limitEnter, this.frames.total]);
        this.transition.onComplete = () => {
          this.$store.commit('SET_STATE_LOADER', false);
          done();
          this.transition.destroy();
          if (!isSafari()) loop.add(this.checkSmooth, 'checkSmooth');
        };
      }
      document.body.classList.remove('wait');
    },

    leave(el, done) {
      this.setAnimation();

      anime({
        target: el,
        duration: 300,
        opacity: 0,
        easing: 'easeInOutSine',
      });
      this.$store.commit('SET_STATE_LOADER', true);
      this.transition.playSegments([0, this.frames.limitLeave]);

      loop.remove(this.checkSmooth, 'checkSmooth');

      setTimeout(() => {
        this.scrollY = 0;
        this.translate = 0;
        window.lerpedScrollY = this.translate;
        window.scroll(0, 0);

        done();
      }, this.frames.totalTime * 450);
    },

    doLoader() {
      this.setAnimation();
      this.$store.commit('SET_STATE_LOADER', true);

      this.transition.play();
      this.transition.onComplete = () => {
        this.$store.commit('SET_STATE_LOADER', false);
        this.transition.destroy();
      };
    },

    scroll(event) {
      if (this.domRendered && this.getSpeakerName === undefined && this.checkScroll) {
        const scroll = this.scrollY + (-1 * event.deltaY);

        this.scrollY = Math.min(Math.max(scroll, 0), this.$refs.smooth.getBoundingClientRect().height - window.innerHeight);
      }
    },

    scrollSafari() {
      this.scrollY = window.pageYOffset;
      window.lerpedScrollY = window.pageYOffset;
    },

    checkSmooth() {
      if (this.placeholderVisible) this.setViewportH();
      if (this.domRendered && Math.round(this.scrollY) !== Math.round(this.translate)) {
        this.translate = roundDec(lerp(this.translate, this.scrollY, isMobileDevice() ? 0.1 : 0.05));
        window.lerpedScrollY = this.translate;
      }
    },

    scrollTo(val) {
      this.scrollY = val;
      if (isSafari()) window.scroll(0, val);
    },

    setTo(val) {
      this.translate = val;
      this.scrollY = val;
    },

    setStateMenu(val) {
      this.menuOpen = val;
    },

    openModal() {
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },

    enterModal(el, done) {
      anime({
        targets: el,
        duration: 700,
        translateY: ['100%', '0%'],
        easing: [0.25, 0.87, 0.43, 1],
        complete: done,
      });
    },

    leaveModal(el, done) {
      anime({
        targets: el,
        duration: 700,
        translateY: ['0%', '100%'],
        easing: [0.25, 0.87, 0.43, 1],
        complete: done,
      });
    },

    setViewportH() {
      this.viewportH = 0;
      if (qs('main') !== null) {
        this.viewportH += qs('main').getBoundingClientRect().height;
      }
      if (qs('.footer') !== null) {
        this.viewportH += qs('.footer').getBoundingClientRect().height;
      }

      this.viewportH = Math.round(this.viewportH);
    },
  },

  beforeDestroy() {
    this.$eventHub.$off('redirect404', this.handleRedirect);
  },
};
</script>

<style lang="scss">
@import "@/styles/_base.scss";

body {
  &.menu-opened,
  &.is-loading {
    overflow: hidden;
    height: 100vh;

    #app {
      overflow: hidden;
      height: 100vh;
    }
  }
  &.wait {
    cursor: progress !important;

    * {
      cursor: progress !important;
    }
  }

  &[js-headervisible] {
    overflow: hidden;
    height: 100vh;
  }
}

.ui-container,
[data-cont] {
  position: relative;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  margin: 0 auto;
  max-width: $container-width;

  &.ui-container--small {
    max-width: $container-width-s;
  }
}

#app {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;

  &.hideOverflow {
    pointer-events: none;
  }

  &.app--fix {
    width: auto;
    height: auto;
    min-height: 100vh;

    // .page-container {
    //   width: 100vw;
    //   height: 100vh;

    //   position: fixed;
    // }
  }
}

.page-container {
  position: relative;
  width: 100%;
}

main {
  overflow: hidden;
}

.loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  overflow: hidden;
  z-index: z-index(loader);

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    width: auto !important;
    height: 120% !important;

    transform: translate3d(-50%, -50%, 0) !important;
  }
}

// .index-block {
//   .i-block__lotti-cont {
//     svg {
//       filter: grayscale(100%);
//       transition: filter 0.5s $global-ease;
//     }
//   }

//   &:hover svg {
//     filter: grayscale(0%);
//   }
// }

[data-cont="big"] {
  max-width: $container-width;
}
[data-cont="small"] {
  max-width: $container-width-s;
}

.blur-text {
  width: 100%;
  font-weight: 700;

  color: transparent;
  font-size: font-size(xsl);
  text-shadow: 0 0 10px rgba(68, 68, 68,0.1);

  @include mq(ipadP) {
    font-size: font-size(xml);
    text-shadow: 0 0 18px rgba(68, 68, 68,0.1);
  }

  @include mq(ds) {
    text-shadow: 0 0 22px rgba(68, 68, 68,0.1);
    font-size: font-size(xxl);
  }
}

[js-anim="text"],
[js-anim="title"],
[js-anim="herotitle"],
[js-anim="herotext"] {
  overflow: hidden;
}

.ui-link {
  text-decoration: none;
  color: $color-text;
  transition: color 0.5s $global-ease 0.1s;

  &::before,
  &::after {
    display: block;
    position: absolute;
    left: 0;
  }

  &::before {
    content: '';
    bottom: -4px;
    width: 100%;
    height: 2px;

    transform: scaleX(0);
    transform-origin: 0% 50%;
    transition: all 0.8s $global-ease;

    @at-root [data-conference="amsterdam"] & {
      background-color: $color-pink;
    }

    @at-root [data-conference="san-francisco"] & {
      background-color: $color-blue;
    }

    @at-root .speakers & {
      background-color: $color-red;
    }
  }

  &::after {
    content: attr(data-hover);
    top: 0;
    width: 0;
    height: 100%;

    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    pointer-events: none;

    transition: all 0.8s $global-ease;

    @at-root [data-conference="amsterdam"] & {
      color: $color-pink;
    }

    @at-root [data-conference="san-francisco"] & {
      color: $color-blue;
    }

    @at-root .speakers & {
      color: $color-red;
    }
  }

  &[data-theme="pink"]::before {
    background-color: $color-pink;
  }

  &[data-theme="pink"]::after {
    color: $color-pink;
  }

  &[data-theme="blue"]::before {
    background-color: $color-blue;
  }

  &[data-theme="blue"]::after {
    color: $color-blue;
  }

  @at-root .pointerevents:not(.touchevents) &:hover,
  .no-pointerevents.no-touchevents &:hover {
    color: transparent;

    &::before {
      transform: scaleX(1);
    }

    &::after {
      width: 102%;
    }
  }

  &.router-link-exact-active {
    pointer-events: none;

    &::before {
      transform: scaleX(1);
    }
  }

  &-alt {
    opacity: 0.75;

    transition: all 0.4s $global-ease;

    &:hover {
      opacity: 1;
    }
  }
}

[data-conference="amsterdam"] {
  ::-moz-selection { // scss-lint:disable VendorPrefix
    color: $color-pink;
  }

  ::selection {
    color: $color-pink;
  }
}

[data-conference="san-francisco"] {
  ::-moz-selection { // scss-lint:disable VendorPrefix
    color: $color-blue;
  }

  ::selection {
    color: $color-blue;
  }
}

.cursor {
  pointer-events: none;
  position: fixed;
  display: block;
  border-radius: 0;
  transform-origin: center center;
  mix-blend-mode: difference;
  // mix-blend-mode: hard-light;
  top: 0;
  left: 0;
  z-index: 1000;
  // filter: url("#goo");

  span {
    position: absolute;
    display: block;
    width: 10px;
    height: 10px;
    border-radius: 100%;
    background-color: transparent;
    transform-origin: center center;
    transform: translate(-50%, -50%);

    @at-root [data-conference="amsterdam"] & {
      background-color: $color-pink;
    }

    @at-root [data-conference="san-francisco"] & {
      background-color: $color-blue;
    }
  }

}

.Cookie.Cookie--min {
  position: fixed;
  width: 100%;
  padding: 5px 20px;

  background: $color-white;
  color: $color-text;
  font-size: 14px;

  z-index: z-index(loader);

  display: flex;
  flex-flow: row;

  overflow: visible;

  @include mq(mobileL) {
    width: 70%;
    max-width: 750px;
    margin: 20px auto;
    padding: 15px 25px;

    font-size: 0.875em;
  }

  a {
    opacity: 0.8;
    font-weight: 500;
    color: $color-text;

    @at-root .pointerevents:not(.touchevents) &:hover,
    .no-pointerevents.no-touchevents &:hover {
      color: $color-black;
    }
  }

  .Cookie__buttons {
    font-size: 1em;
    margin-left: 20px;

    padding: 0;
    font-weight: 400;

    transition: color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);

    cursor: pointer;

    @include mq(mobileL) {
      padding: 0;
    }

    .cookie-button {
      appearance: none;
      border: 0;
      cursor: pointer;

      padding: 12px 22px;
      margin: 0px !important;

      background: darken($color-light, 5%);
      color: $color-black;

      &:hover {
        background: darken($color-light, 10%);
      }
    }
  }
}
</style>
